import { produce } from 'immer'
import _ from 'lodash'
import { create } from 'zustand'

import {
  createDefaultPanel,
  generateId,
  NO_GHOST_FILTERS,
} from 'features/charts-v2/store'
import type {
  NumericOperatorType,
  OperatorType,
  SelectedFilterOption,
} from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { FeedbackControlsConfig } from 'types/project'

export type FeedbackControlsState = {
  feedbackControls: FeedbackControlsConfig[]
  setFeedbackControls: (values: FeedbackControlsConfig[]) => void
  updateFeedbackControls: (
    comparativeIndex: number,
    field: string,
    value: string | number | boolean | null,
  ) => void
  resetFeedbackControls: () => void
}

export const initialFeedbackControlsState = {
  feedbackControls: [
    { order: 'desc', orderField: 'create_time' },
  ] as FeedbackControlsConfig[],
}

export const useFeedbackControlsStore = create<FeedbackControlsState>()(
  (set) => ({
    ...initialFeedbackControlsState,

    setFeedbackControls: (values) =>
      set(
        produce((s: FeedbackControlsState) => {
          s.feedbackControls = values
        }),
      ),

    updateFeedbackControls: (comparativeIndex, field, value) =>
      set(
        produce((s: FeedbackControlsState) => {
          ;(s.feedbackControls[comparativeIndex] as Record<string, unknown>)[
            _.camelCase(field)
          ] = value
        }),
      ),

    resetFeedbackControls: () => set(initialFeedbackControlsState),
  }),
)

// ---------------------------------------------------------------------------
// Feedback's filter state is the same `ChartPanel` shape charts-v2 uses — see
// client/AGENTS.md's Feedback-sidebar plan: reusing the type gives Feedback
// the existing v1<->v2 translators, per-panel filter option counts, and the
// summarize/smart-search plumbing for free, at the cost of a few inert
// chart-only fields (chartType/metrics/breakdowns) riding along unused.
// Feedback never renders UI for those fields.
//
// `panels.length` is reconciled to `usePanelStore.comparativePanelsNumber`
// (the shared toolbar's add/remove-panel authority) via `setPanelCount`,
// called from `use-feedback-state.ts`. This store does not add or remove
// panels on its own initiative — only the filter *contents* of each slot.
// ---------------------------------------------------------------------------

export type FeedbackFiltersState = {
  panels: ChartPanel[]
  activePanelId: string | null
  /** Eye-toggled-off filters per panel. See `charts-v2/store.ts`'s
   *  `ghostFilters` doc comment for why this is a sibling of `panels`,
   *  never persisted onto a filter itself. */
  ghostFilters: Record<string, SelectedFilterOption[]>

  hydrate: (state: {
    panels: ChartPanel[]
    activePanelId: string | null
  }) => void
  /** Reconciles `panels.length` to `n`, growing by cloning `panels[0]` (or
   *  seeding a fresh default panel when empty) and shrinking by truncating
   *  from the end. For removing one specific panel, use `removePanelAt`. */
  setPanelCount: (n: number) => void
  /** Removes the panel at `index` (mirrors `usePanelStore.removePanel`'s
   *  index-based removal) and reassigns `order` on the survivors. */
  removePanelAt: (index: number) => void
  updatePanel: (panelId: string, updates: Partial<ChartPanel>) => void
  updateFilterSelection: (
    panelId: string,
    field: string,
    operator: OperatorType | NumericOperatorType,
    values: string[],
    range?: {
      min?: number
      max?: number
      valueType?: number
      numericValue?: number
    },
  ) => void
  syncFiltersToOtherPanels: (sourcePanelId: string) => void
  disableFilter: (panelId: string, field: string) => void
  enableFilter: (panelId: string, field: string) => void
  dropGhost: (panelId: string, field: string) => void
  setActivePanelId: (id: string | null) => void
  toHashPayload: () => { panels: ChartPanel[]; activePanelId: string | null }
  resetFeedbackFilters: () => void
}

const initialFeedbackFiltersState = {
  panels: [] as ChartPanel[],
  activePanelId: null as string | null,
}

/** Same rule as `charts-v2/store.ts`'s `pruneGhostFilters`: every
 *  `ghostFilters` entry names a live panel, and only fields NOT currently in
 *  that panel's `filters`. Run after every mutation that can add, remove, or
 *  replace a panel's filters. */
function pruneGhostFilters(state: FeedbackFiltersState): void {
  for (const panelId of Object.keys(state.ghostFilters)) {
    const panel = state.panels.find((p) => p.id === panelId)
    if (!panel) {
      delete state.ghostFilters[panelId]
      continue
    }
    const liveFields = new Set(panel.filters.map((f) => f.field))
    const ghosts = state.ghostFilters[panelId]!
    const kept = ghosts.filter((f) => !liveFields.has(f.field))
    if (kept.length === 0) {
      delete state.ghostFilters[panelId]
    } else if (kept.length !== ghosts.length) {
      state.ghostFilters[panelId] = kept
    }
  }
}

export const useFeedbackFiltersStore = create<FeedbackFiltersState>()((
  set,
  get,
) => {
  const setPanels = (recipe: (state: FeedbackFiltersState) => void): void => {
    set(
      produce((state: FeedbackFiltersState) => {
        recipe(state)
        pruneGhostFilters(state)
      }),
    )
  }

  return {
    ...initialFeedbackFiltersState,
    ghostFilters: {},

    hydrate: ({ panels, activePanelId }) => {
      set({
        panels,
        activePanelId,
        // Mirrors charts-v2: a hash pop restores the same generated panel
        // ids, so ghosts for those panels survive; never a blanket clear.
        ghostFilters: Object.fromEntries(
          Object.entries(get().ghostFilters).filter(([panelId, ghosts]) => {
            const panel = panels.find((p) => p.id === panelId)
            return !!panel && ghosts.length > 0
          }),
        ),
      })
    },

    setPanelCount: (n) => {
      setPanels((state) => {
        if (n === state.panels.length) return
        if (n < state.panels.length) {
          state.panels = state.panels.slice(0, Math.max(n, 0))
          if (!state.panels.some((p) => p.id === state.activePanelId)) {
            state.activePanelId = state.panels[0]?.id ?? null
          }
          return
        }
        const seed = state.panels[0]
        while (state.panels.length < n) {
          const order = state.panels.length
          const cloned: ChartPanel = seed
            ? { ...JSON.parse(JSON.stringify(seed)), id: generateId(), order }
            : createDefaultPanel(order)
          state.panels.push(cloned)
        }
        if (!state.activePanelId) {
          state.activePanelId = state.panels[0]?.id ?? null
        }
      })
    },

    removePanelAt: (index) => {
      setPanels((state) => {
        if (state.panels.length <= 1) return
        const removed = state.panels[index]
        state.panels = state.panels.filter((_, i) => i !== index)
        state.panels.forEach((panel, i) => {
          panel.order = i
        })
        if (removed && state.activePanelId === removed.id) {
          state.activePanelId = state.panels[0]?.id ?? null
        }
      })
    },

    updatePanel: (panelId, updates) => {
      setPanels((state) => {
        const panelIndex = state.panels.findIndex((p) => p.id === panelId)
        if (panelIndex !== -1) {
          state.panels[panelIndex] = {
            ...state.panels[panelIndex]!,
            ...updates,
          }
        }
      })
    },

    updateFilterSelection: (panelId, field, operator, values, range) => {
      setPanels((state) => {
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return

        const existingFilters = panel.filters || []
        const existingIndex = existingFilters.findIndex(
          (f) => f.field === field,
        )

        // Detect delete: empty values AND no range of any kind. Window
        // filters (score/post_score) send empty values + {min,max,valueType}
        // and numeric filters send empty values + {numericValue}; either is
        // an update, not a delete. Mirrors charts-v2/store.ts verbatim.
        const isDeleteOperation =
          values.length === 0 &&
          range?.numericValue === undefined &&
          range?.min === undefined &&
          range?.max === undefined &&
          range?.valueType === undefined

        if (isDeleteOperation && existingIndex >= 0) {
          panel.filters = existingFilters.filter((f) => f.field !== field)
          return
        }

        const newFilter = {
          field,
          operator,
          values,
          ...(range || {}),
        }

        let newFilters
        if (existingIndex >= 0) {
          newFilters = [...existingFilters]
          newFilters[existingIndex] = newFilter
        } else {
          newFilters = [...existingFilters, newFilter]
        }

        panel.filters = newFilters
      })
    },

    syncFiltersToOtherPanels: (sourcePanelId) => {
      setPanels((state) => {
        const source = state.panels.find((p) => p.id === sourcePanelId)
        if (!source) return
        for (const panel of state.panels) {
          if (panel.id === sourcePanelId) continue
          const cloned = JSON.parse(
            JSON.stringify({
              filters: source.filters,
              searchTerms: source.searchTerms,
              searchCriteria: source.searchCriteria,
              smartSearchEnabled: source.smartSearchEnabled,
            }),
          )
          panel.filters = cloned.filters
          panel.searchTerms = cloned.searchTerms
          panel.searchCriteria = cloned.searchCriteria
          panel.smartSearchEnabled = cloned.smartSearchEnabled
        }
      })
    },

    disableFilter: (panelId, field) => {
      setPanels((state) => {
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return
        const index = panel.filters.findIndex((f) => f.field === field)
        if (index === -1) return
        const [ghosted] = panel.filters.splice(index, 1)
        if (!ghosted) return
        const existing = state.ghostFilters[panelId] ?? []
        state.ghostFilters[panelId] = [
          ...existing.filter((f) => f.field !== field),
          ghosted,
        ]
      })
    },

    enableFilter: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const index = ghosts.findIndex((f) => f.field === field)
        if (index === -1) return
        const [restored] = ghosts.splice(index, 1)
        if (!restored) return
        if (ghosts.length === 0) delete state.ghostFilters[panelId]
        const panel = state.panels.find((p) => p.id === panelId)
        if (!panel) return
        panel.filters = [
          ...panel.filters.filter((f) => f.field !== field),
          restored,
        ]
      })
    },

    dropGhost: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const kept = ghosts.filter((f) => f.field !== field)
        if (kept.length === 0) delete state.ghostFilters[panelId]
        else state.ghostFilters[panelId] = kept
      })
    },

    setActivePanelId: (id) => {
      set(
        produce((state: FeedbackFiltersState) => {
          state.activePanelId = id
        }),
      )
    },

    toHashPayload: () => {
      const state = get()
      return { panels: state.panels, activePanelId: state.activePanelId }
    },

    resetFeedbackFilters: () => {
      set({ ...initialFeedbackFiltersState, ghostFilters: {} })
    },
  }
})

/** Stable empty-array re-export so `ghostFilters[panelId] ?? NO_GHOST_FILTERS`
 *  selectors never mint a fresh identity. Same constant charts-v2 uses. */
export { NO_GHOST_FILTERS }
